import { LocalStorageKeys } from "@/utils/local-storage-keys";

const POST_VIEW_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 14;
const POST_VIEW_CACHE_MAX_ENTRIES = 5_000;

type PostViewCache = Record<string, number>;

type PostViewCacheEntry = {
  postHash: string;
  publicKey: string;
};

const getPostViewCacheEntryKey = ({
  postHash,
  publicKey,
}: PostViewCacheEntry) => `${publicKey}:${postHash}`;

const readPostViewCache = (): PostViewCache => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(LocalStorageKeys.postViews);
    const parsed = rawValue ? JSON.parse(rawValue) : {};

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    const entries = Object.entries(parsed).filter(
      (entry): entry is [string, number] => {
        const [, timestamp] = entry;
        return typeof timestamp === "number" && Number.isFinite(timestamp);
      },
    );

    return Object.fromEntries(entries);
  } catch {
    return {};
  }
};

const writePostViewCache = (cache: PostViewCache) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      LocalStorageKeys.postViews,
      JSON.stringify(cache),
    );
  } catch {
    // Best-effort cache only.
  }
};

const prunePostViewCache = (
  cache: PostViewCache,
  now = Date.now(),
): PostViewCache => {
  const freshEntries = Object.entries(cache).filter(([, timestamp]) => {
    return now - timestamp <= POST_VIEW_CACHE_MAX_AGE_MS;
  });

  if (freshEntries.length <= POST_VIEW_CACHE_MAX_ENTRIES) {
    return Object.fromEntries(freshEntries);
  }

  return Object.fromEntries(
    freshEntries
      .sort(([, leftTimestamp], [, rightTimestamp]) => {
        return rightTimestamp - leftTimestamp;
      })
      .slice(0, POST_VIEW_CACHE_MAX_ENTRIES),
  );
};

export const hasRecentlySentPostView = (entry: PostViewCacheEntry) => {
  const cache = readPostViewCache();
  const prunedCache = prunePostViewCache(cache);
  const cacheKey = getPostViewCacheEntryKey(entry);

  if (Object.keys(prunedCache).length !== Object.keys(cache).length) {
    writePostViewCache(prunedCache);
  }

  return Boolean(prunedCache[cacheKey]);
};

export const recordSentPostView = (entry: PostViewCacheEntry) => {
  recordSentPostViews([entry]);
};

export const recordSentPostViews = (entries: PostViewCacheEntry[]) => {
  const cache = prunePostViewCache(readPostViewCache());

  const now = Date.now();

  for (const entry of entries) {
    cache[getPostViewCacheEntryKey(entry)] = now;
  }

  writePostViewCache(prunePostViewCache(cache));
};
